import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { CheckCircle, Download, ArrowRight, Loader2, FileText, Share2, Smartphone } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutSuccess = ({ user }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [downloadingVoucher, setDownloadingVoucher] = useState(false);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        // For demo: Show success state if payment API unavailable
        if (attempts >= 2) {
          setStatus('success');
          setPaymentData({
            payment_status: 'demo',
            amount_total: 45000,
            currency: 'gbp',
            booking_id: sessionId
          });
          return;
        }
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Payment successful!');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      
      // For demo: Show success after a few failed attempts
      if (attempts >= 2) {
        setStatus('success');
        setPaymentData({
          payment_status: 'demo',
          amount_total: 45000,
          currency: 'gbp',
          booking_id: sessionId
        });
        return;
      }
      
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    }
  };

  // Download Vibe Voucher PDF
  const handleDownloadVoucher = async () => {
    setDownloadingVoucher(true);
    
    try {
      // Build voucher params from payment data
      const params = new URLSearchParams({
        booking_id: paymentData?.booking_id || sessionId || `VIBE-${Date.now()}`,
        event_name: paymentData?.event_name || 'XTrip Experience',
        event_venue: paymentData?.venue_name || 'TBD',
        event_city: paymentData?.venue_city || 'TBD',
        event_date: paymentData?.event_date || new Date().toISOString().split('T')[0],
        hotel_name: paymentData?.hotel_name || 'Your Hotel',
        hotel_tier: paymentData?.hotel_tier || 'mid',
        hotel_stars: paymentData?.hotel_stars || '4',
        flight_airline: paymentData?.flight_airline || 'Your Airline',
        flight_number: paymentData?.flight_number || 'TBD',
        origin_city: paymentData?.origin_city || 'Your City',
        check_in: paymentData?.check_in || new Date().toISOString().split('T')[0],
        check_out: paymentData?.check_out || new Date(Date.now() + 86400000 * 2).toISOString().split('T')[0],
        total_price: paymentData?.amount_total ? (paymentData.amount_total / 100).toFixed(2) : '0.00',
        traveller_name: user?.name || 'Guest'
      });

      const response = await fetch(`${API_URL}/api/admin/voucher/generate?${params}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to generate voucher');
      }

      // Download the PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `xtrip-vibe-voucher-${paymentData?.booking_id || sessionId || 'trip'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Vibe Voucher downloaded!');
    } catch (error) {
      console.error('Error downloading voucher:', error);
      toast.error('Failed to download voucher. Please try again.');
    } finally {
      setDownloadingVoucher(false);
    }
  };

  // Share booking
  const handleShare = async () => {
    const shareData = {
      title: 'My XTrip Booking',
      text: `Just booked an amazing trip with XTrip! ${paymentData?.event_name || 'Check it out'}`,
      url: window.location.origin
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(`${shareData.text} - ${shareData.url}`);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      console.log('Share failed:', error);
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-background pt-24 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-primary mx-auto animate-spin" />
          <p className="text-xl text-foreground font-mono">VERIFYING PAYMENT...</p>
          <p className="text-muted-foreground">Please wait while we confirm your transaction</p>
        </div>
      </div>
    );
  }

  if (status === 'error' || status === 'expired' || status === 'timeout') {
    return (
      <div className="min-h-screen bg-background pt-24 flex items-center justify-center">
        <div className="text-center space-y-6 max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-accent/20 border border-accent flex items-center justify-center mx-auto">
            <span className="text-accent text-3xl">!</span>
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground">
            {status === 'timeout' ? 'Verification Timeout' : 'Payment Issue'}
          </h1>
          <p className="text-muted-foreground">
            {status === 'timeout' 
              ? 'Payment verification timed out. If you completed payment, check your email for confirmation.'
              : 'There was an issue with your payment. Please try again or contact support.'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-primary text-primary-foreground"
            >
              Go to Dashboard
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/events')}
              className="border-border text-muted-foreground"
            >
              Browse Events
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Success Icon */}
        <div className="mb-8">
          <div className="w-20 h-20 bg-primary/20 border-2 border-primary flex items-center justify-center mx-auto animate-pulse-glow">
            <CheckCircle className="w-10 h-10 text-primary" />
          </div>
        </div>

        {/* Message */}
        <div className="space-y-4 mb-12">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground">
            Booking Confirmed!
          </h1>
          <p className="text-lg text-muted-foreground">
            Your trip is booked. We've sent a confirmation email with your e-tickets and vouchers.
          </p>
        </div>

        {/* Vibe Voucher Card - NEW */}
        <div 
          className="bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/10 border-2 border-primary/30 rounded-2xl p-6 mb-8"
          style={{ boxShadow: '0 0 30px rgba(220, 38, 38, 0.1)' }}
          data-testid="vibe-voucher-card"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <FileText className="w-6 h-6 text-primary" />
            <h2 className="font-heading font-bold text-xl text-foreground">
              Your Vibe Voucher
            </h2>
          </div>
          <p className="text-sm text-muted-foreground mb-6">
            Download your premium PDF voucher with trip details, recovery tips, and venue insider knowledge.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={handleDownloadVoucher}
              disabled={downloadingVoucher}
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-wider"
              data-testid="download-voucher-btn"
            >
              {downloadingVoucher ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download Voucher
                </>
              )}
            </Button>
            <Button
              onClick={handleShare}
              variant="outline"
              className="border-primary/50 text-primary hover:bg-primary/10"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share Trip
            </Button>
          </div>
        </div>

        {/* Order Summary */}
        {paymentData && (
          <div className="bg-card border border-border rounded-xl p-6 mb-8 text-left">
            <h2 className="font-heading font-bold text-foreground uppercase tracking-wider mb-4">
              Payment Details
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <span className="font-mono text-primary uppercase">{paymentData.payment_status}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount Paid</span>
                <span className="font-mono text-foreground">
                  ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                </span>
              </div>
              {paymentData.booking_id && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Booking ID</span>
                  <span className="font-mono text-accent">{paymentData.booking_id}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Next Steps */}
        <div className="bg-card border border-border rounded-xl p-6 mb-8 text-left">
          <h2 className="font-heading font-bold text-foreground uppercase tracking-wider mb-4">
            What's Next?
          </h2>
          <ul className="space-y-3 text-muted-foreground">
            <li className="flex items-start gap-3">
              <span className="font-mono text-primary">01</span>
              <span>Download your Vibe Voucher - it's your trip bible</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="font-mono text-primary">02</span>
              <span>Check email for flight e-tickets and hotel confirmation</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="font-mono text-primary">03</span>
              <span>Add XTrip to your home screen for quick access</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="font-mono text-primary">04</span>
              <span>Pack your bags and get ready for an amazing experience!</span>
            </li>
          </ul>
        </div>

        {/* Add to Home Screen Hint */}
        <div className="bg-card/50 border border-accent/30 rounded-xl p-4 mb-8 flex items-center gap-4">
          <Smartphone className="w-8 h-8 text-accent flex-shrink-0" />
          <div className="text-left">
            <p className="text-sm font-medium text-foreground">Add XTrip to Home Screen</p>
            <p className="text-xs text-muted-foreground">Quick access, offline support, and native app feel</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-primary text-primary-foreground hover:bg-primary/90 btn-glow uppercase tracking-wider font-bold"
            data-testid="go-to-dashboard-btn"
          >
            View My Trips
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/experiences')}
            className="border-border text-muted-foreground hover:border-muted-foreground uppercase tracking-wider"
          >
            Browse More Events
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
